// @ts-strict-ignore
import { component } from "picoapp";
import {
  type Context,
  type ImageMedia,
  type SelectedVariant,
} from "../../state";
import {
  currencySymbol,
  findSelectedVariants,
  formatMoney,
  formatMsrpValue,
  getVariantMetadata,
  intelligemsCompareAtPrice,
  intelligemsPrice,
  updatePictureElement,
} from "../../utilities";
import {
  InnerArrows,
  RadioSlides,
} from "../Options/tbsp-product-options-carousel";
import Splide from "@splidejs/splide";
import { TablescapeContext, TablescapeEvent } from "@tablescape/context";
import { STOREFRONT_CART_ATTRIBUTES } from "@tablescape/constants";
import { type CountryCode } from "@shopify/hydrogen-react/storefront-api-types";

const currency = currencySymbol();

export const productHighlightedUpsellPrice = component(
  (node: HTMLElement, context: Context) => {
    const priceTextNode = node.querySelector("[data-tbsp-component='text']");
    const productIdData = node.dataset.productId;

    context.on(
      TablescapeEvent.HIGHLIGHTED_UPSELL_VARIANT_PRICE_UPDATED,
      ({ selectedVariant }) => {
        if (!selectedVariant) {
          return;
        }
        let { compareAtPrice, price, productId } = selectedVariant;
        if (productIdData !== productId) return;
        const { discounts } = context.getState();

        const discount = discounts?.find(
          (discount) => discount.productId === productId
        );

        const hasDiscount =
          discount && discount.type === node.dataset.discountType;

        if (window.igData) {
          const igPrice = intelligemsPrice([selectedVariant]);
          const igComparePrice = intelligemsCompareAtPrice([selectedVariant]);
          price = igPrice || selectedVariant.price;
          compareAtPrice = igComparePrice || compareAtPrice;
        }

        if (hasDiscount) {
          price -= discount.value;
        }

        if (priceTextNode) {
          priceTextNode.textContent = price
            ? `${currency}${formatMoney(
                price,
                "{{ amount_no_trailing_zeroes }}",
                false
              )}`
            : "";
        }

        if (compareAtPrice || hasDiscount) {
          priceTextNode?.classList.add("text-interactive-brick");
        } else {
          priceTextNode?.classList.remove("text-interactive-brick");
        }
      }
    );
  }
);

export const productHighlightedUpsellCompareAtPrice = component(
  (node: HTMLElement, context: Context) => {
    const priceTextNode = node.querySelector("[data-tbsp-component='text']");
    const productIdData = node.dataset.productId;

    context.on(
      TablescapeEvent.HIGHLIGHTED_UPSELL_VARIANT_PRICE_UPDATED,
      ({ selectedVariant }) => {
        if (!selectedVariant) return;
        if (productIdData !== selectedVariant.productId) return;
        const { discounts } = context.getState();
        const discount = discounts?.find(
          (discount) => discount.productId === selectedVariant.productId
        );

        let price = selectedVariant.price;
        let _compareAtPrice = selectedVariant.compareAtPrice;

        if (window.igData) {
          const igPrice = intelligemsPrice([selectedVariant]);
          const igComparePrice = intelligemsCompareAtPrice([selectedVariant]);
          price = igPrice || selectedVariant.price;
          _compareAtPrice = igComparePrice || _compareAtPrice;
        }

        let compareAtPrice = _compareAtPrice;
        if (
          discount &&
          discount.type === node.dataset.discountType &&
          !_compareAtPrice
        ) {
          if (!_compareAtPrice) {
            compareAtPrice = price;
          }
        }
        if (priceTextNode) {
          priceTextNode.textContent = compareAtPrice
            ? `${currency}${formatMoney(
                compareAtPrice,
                "{{ amount_no_trailing_zeroes }}",
                false
              )}`
            : "";
        }
      }
    );
  }
);

export const productHighlightedUpsell = component<Context>(
  async (node, context) => {
    const form = node.querySelector<HTMLFormElement>("form");
    const fetchController = new AbortController();

    const discountPrice = node.dataset.discountPrice;
    const productHandle = node.dataset.productHandle;

    const productSwatchOptions = node.querySelectorAll<HTMLElement>(
      "div[data-mtr-target='swatch']"
    );

    if (discountPrice) {
      const discount = {
        name: productHandle,
        type: "productHighlightedUpsell",
        value: parseInt(discountPrice),
        productId: node.dataset.productId,
      };
      const state = context.getState();
      const discounts = [...state.discounts, discount];
      context.hydrate({ ...state, discounts });
      TablescapeContext.emit(TablescapeEvent.UPDATE_DISCOUNTS, {
        discounts,
      });
    }

    const getSelectedVariant = async (
      element: HTMLElement
    ): Promise<SelectedVariant> => {
      const country = document.documentElement.dataset
        .countryCode as CountryCode;
      const variantName = element.dataset.variantOption.replace(
        `${productHandle}:`,
        ""
      );
      const variantValue = element.dataset.productVariantTitle;
      const variantId = element.dataset.productVariantId;
      const selectedVariant = await getVariantMetadata(
        {
          country,
          productHandle,
          selectedOptions: [{ name: variantName, value: variantValue }],
          variantId,
        },
        { abortController: fetchController }
      );
      return selectedVariant;
    };

    const updateCurrentSwatchOption = (target: HTMLElement) => {
      productSwatchOptions.forEach((option: HTMLElement) => {
        option.parentElement.removeAttribute("aria-current");
      });
      target.parentElement.setAttribute("aria-current", "true");
    };

    const updateImage = (selectedVariant: SelectedVariant) => {
      const imageElement = node.querySelector<HTMLImageElement>(
        "[data-tbsp-component='image']"
      );
      const imageMedia = <ImageMedia>(
        selectedVariant.media.find((media) => media.contentType === "IMAGE")
      );

      if (imageElement) {
        updatePictureElement({
          element: imageElement,
          source: imageMedia.image,
        });
      }
    };

    const updateShippingNote = (selectedVariant: SelectedVariant) => {
      const shippingNoteElement = node.querySelector("[data-shipping-note]");

      if (selectedVariant.shippingNote) {
        shippingNoteElement.querySelector(
          "[data-tbsp-component='text']"
        ).textContent = selectedVariant.shippingNote;
        shippingNoteElement.classList.remove("hidden");
        shippingNoteElement.classList.add("block");
      } else {
        shippingNoteElement.classList.remove("block");
        shippingNoteElement.classList.add("hidden");
      }
    };

    const handleSwatchClick = async ({
      target,
    }: InputEvent & { target: HTMLInputElement }) => {
      const selectedVariant = await getSelectedVariant(target);
      const select = form.querySelector<HTMLSelectElement>(
        `select[data-product-handle=${productHandle}]`
      );
      if (select && select.options) {
        const selectedIndex = Array.from(select.options).findIndex(
          (option) => option.value === selectedVariant.id
        );
        select.selectedIndex = selectedIndex;
      }
      updateCurrentSwatchOption(target);
      updateImage(selectedVariant);
      updateShippingNote(selectedVariant);

      if (selectedVariant) {
        const { discounts, products } = context.getState();

        context.emit(TablescapeEvent.VARIANT_PRICE_UPDATED, {
          discounts,
          products,
        });
      }

      setIsUpsellProductAvailable(selectedVariant?.availableForSale);
      updateFormButtons();
    };

    const setIsMainProductAvailable = (isAvailable: boolean) => {
      node.dataset.isMainProductAvailable = String(isAvailable);
    };

    const setIsUpsellProductAvailable = (isAvailable: boolean) => {
      node.dataset.isUpsellProductAvailable = String(isAvailable);
    };

    const updateFormButtons = () => {
      const addToCartButton = form.querySelector<HTMLDivElement>(
        "[data-highlighted-upsell-form-button-atc]"
      );
      const oosButton = form.querySelector<HTMLDivElement>(
        "[data-highlighted-upsell-form-button-oos]"
      );

      const isMainProductAvailable =
        node.dataset.isMainProductAvailable === "true";
      const isUpsellProductAvailable =
        node.dataset.isUpsellProductAvailable === "true";

      if (isMainProductAvailable && isUpsellProductAvailable) {
        addToCartButton.classList.remove("hidden");
        oosButton.classList.add("hidden");
      } else {
        addToCartButton.classList.add("hidden");
        oosButton.classList.remove("hidden");
      }
    };

    const handleSubmit = async (e: SubmitEvent) => {
      e.preventDefault();
      const selectFields = document.querySelectorAll<HTMLSelectElement>(
        `select[name="variant-options"]`
      );

      const variants = [...selectFields]
        .filter((select) => select.selectedOptions.length > 0)
        .map((select) => ({
          id: select.selectedOptions[0].value,
          msrp: select.selectedOptions[0].dataset.productVariantMsrp,
          hasShippingNote:
            select.selectedOptions[0].dataset.hasShippingNote === "true",
        }));
      if (!variants.length) return;

      const isMultiQuantitySet =
        [...new Set(variants.map((varaint) => varaint.id))].length === 1;

      const variantsToUse = isMultiQuantitySet
        ? variants.slice(0, 1)
        : variants;

      const bundleTimestamp = Date.now();

      const lines = variantsToUse.map((variant, index) => ({
        attributes: [
          {
            key: STOREFRONT_CART_ATTRIBUTES.MSRP,
            value: formatMsrpValue(variant.msrp),
          },
          {
            key: STOREFRONT_CART_ATTRIBUTES.TIMESTAMP,
            value: String(bundleTimestamp),
          },
          ...(index === 0
            ? [
                {
                  key: STOREFRONT_CART_ATTRIBUTES.RELATED_ITEMS,
                  value: variantsToUse.map((variant) => variant.id).join(";"),
                },
              ]
            : []),
          ...(variant.hasShippingNote
            ? [
                {
                  key: STOREFRONT_CART_ATTRIBUTES.PREORDER,
                  value: String(true),
                },
              ]
            : []),
        ],
        id: variant.id,
        quantity: isMultiQuantitySet ? variants.length : 1,
      }));

      try {
        requestAnimationFrame(() => {
          TablescapeContext.emit(TablescapeEvent.TOGGLE_CART, {
            ui: {
              cartDrawerOpen: true,
              cartDrawerUserToggled: false,
              giftNoteDrawerOpen: false,
              productVariantDrawerOpen: false,
            },
          });
          TablescapeContext.emit(TablescapeEvent.ADD_TO_CART, {
            cart: {
              lines,
            },
          });
        });
      } catch (e) {
        console.error(e);
      }
    };

    const initializeCurrentSwatchOption = () => {
      const select = form.querySelector<HTMLSelectElement>(
        `select[data-product-handle=${productHandle}]`
      );
      let selectedIndex = 0;
      if (select && select.options) {
        selectedIndex = Array.from(select.options).findIndex(
          (option) => option.selected
        );
      }
      productSwatchOptions[selectedIndex].click();
    };

    const isMainProductVariantBundle =
      document.querySelector<HTMLFormElement>(`form[id="product-form"]`)
        ?.dataset.variantBundle === "true";

    context.on(TablescapeEvent.VARIANT_SELECTED, async (data?) => {
      if (!data || !data.products || !data.selectedVariant) return;

      const mainProductSelectedVariants = findSelectedVariants(data.products);

      setIsMainProductAvailable(
        mainProductSelectedVariants.every((variant) =>
          isMainProductVariantBundle
            ? variant.variantBundleAvailableForSale
            : variant.availableForSale
        )
      );
      updateFormButtons();
    });

    const productOptionsElement = node.querySelector<HTMLElement>(
      "[data-product-options]"
    );

    const optionsSlider = new Splide(productOptionsElement, {
      perPage: 4,
      padding: 4,
      fixedHeight: 30,
      arrows: false,
      drag: false,
      pagination: false,
      gap: 14,
      mediaQuery: "min",
      breakpoints: {
        750: {
          perPage: 7,
        },
      },
    });

    optionsSlider.on("mounted", () => {
      productOptionsElement.classList.replace("opacity-0", "opacity-100");

      if (productSwatchOptions.length) {
        productSwatchOptions.forEach((option: HTMLElement) =>
          option.addEventListener("click", handleSwatchClick)
        );
        initializeCurrentSwatchOption();
      }
    });

    optionsSlider.mount({
      InnerArrows: InnerArrows(node.dataset.nextLabel, node.dataset.prevLabel),
      RadioSlides,
    });

    form.addEventListener("submit", handleSubmit);

    return () => {
      form.removeEventListener("submit", handleSubmit);
      productSwatchOptions.forEach((option: HTMLElement) =>
        option.removeEventListener("click", handleSwatchClick)
      );
    };
  }
);
